import { z } from "zod";

/**
 * The doorman's <assessment> trailer — PRD FR-2.3 output contract.
 * Parsed server-side, NEVER forwarded to a sender (PRD §5.4 hard rule 1).
 */
export const WorkRequestKind = z.enum(["document_review", "claim_verification", "source_research"]);

/** A claim is one checkable statement, in the knocker's own words. */
export const CLAIM_MAX_CHARS = 300;
/** What one researched source is asked to answer. */
export const RESEARCH_QUESTION_MAX_CHARS = 200;
/**
 * How many claims or sources ONE request may name. There is no ceiling on work
 * (R-0905-11) and this is not one: it is the shape of a single request, and a
 * doorman with an eleventh claim makes an eleventh request.
 */
export const WORK_ITEMS_MAX = 10;

/**
 * THE DOORMAN NAMES WHAT IT CHECKS (ruling R-0910-12 rule 1).
 *
 * A verification used to be a NUMBER — `{"kind":"claim_verification","units":3}`
 * — and the server picked the three claims by taking the first sentences of
 * whatever the knocker had last typed. A knock that invented a project at a URL
 * was therefore checked, when it was checked at all, against whichever
 * sentences came first, and the URL it named was never opened by anybody.
 *
 * So the request carries the claims themselves. Each claim is one checkable
 * statement the knocker made, IN THE KNOCKER'S WORDS, with the URL they
 * attached to it when they gave one — a bare link with no claim around it is
 * the claim "this link is what they say it is". A research request carries the
 * URLs and the question each one should answer. `units` survives only for
 * `document_review`, where it is a page count; for the other two the units ARE
 * the array's length, and the server derives nothing at all.
 */
export const WorkRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("document_review"),
    units: z.number().int().min(0),
    reason: z.string(),
    /**
     * Which attachment a `document_review` reads (ruling R-0905-11): the id
     * the server put in the attachment's notice line. A knock with several
     * documents reviews each by name; absent, the most recent document.
     */
    attachment_id: z.string().optional(),
  }),
  z.object({
    kind: z.literal("claim_verification"),
    reason: z.string(),
    claims: z
      .array(
        z.object({
          claim: z.string().min(1).max(CLAIM_MAX_CHARS),
          url: z.string().nullable(),
          /**
           * THE DOORMAN MAY ASK FOR X SEARCH ON A CLAIM (ruling R-0911-1).
           *
           * Checks run with web search alone. X Search is added to a call when
           * the claim or its link is ABOUT X — which the server detects from the
           * URL and the words — or when the doorman says so here, for the claim
           * whose evidence is on X and says so nowhere a pattern can see it
           * ("their account was created last spring"). Optional and absent by
           * default: a claim that does not need X does not carry the field, and
           * a missing field is never read as `true`.
           */
          x_search: z.boolean().optional(),
        }),
      )
      .min(1)
      .max(WORK_ITEMS_MAX),
  }),
  z.object({
    kind: z.literal("source_research"),
    reason: z.string(),
    sources: z
      .array(
        z.object({
          url: z.string(),
          question: z.string().min(1).max(RESEARCH_QUESTION_MAX_CHARS),
        }),
      )
      .min(1)
      .max(WORK_ITEMS_MAX),
  }),
]);
export type WorkRequest = z.infer<typeof WorkRequest>;

/**
 * The rubric's ranges (FR-2.3 as amended by ruling R-0908-5). A value over its
 * top is clamped DOWN to it.
 *
 * SIXTY POINTS USED TO RIDE ON PROSE. The old rubric weighted relevance 30 and
 * specificity 25 against a credibility worth 10, so the harness (2026-09-07/09)
 * watched doormen refuse a scammer in words — "the number shifting under
 * questioning is on the record, and it counts" — and then score them 74,
 * because a fluent, on-topic pitch collected the fluent, on-topic points. The
 * weights now say what the doorman was already saying: receipts first, what
 * stands after a refusal second, and five points for saying it clearly.
 */
export const DIMENSION_MAX = {
  verification: 30,
  substance: 25,
  compatibility: 25,
  value: 15,
  clarity: 5,
} as const;

export const SUMMARY_MAX_CHARS = 200;
export const IMPROVEMENT_MAX_CHARS = 160;
export const OVERRIDE_LINE_MAX_CHARS = 200;

/**
 * THE DOORMAN'S OWN QUESTION, COPIED (ruling R-0925-2 (1)). A receipt counts as
 * refused or unusable only where a doorman message EARLIER in the hearing asked
 * for it, and the server checks that against the transcript the way it checks
 * a changed story (`knocks/scoring.ts#askedInVerified`). A quotation is cut,
 * not refused, past its cap: a cut quote is still a run of the doorman's words
 * and is found exactly where the whole one would be.
 */
export const ASKED_IN_MAX_CHARS = 300;

/**
 * THE OVERRIDE, AS A FIELD (ruling R-0910-4).
 *
 * An override used to be a thing the doorman was told to honour in prose and
 * then had to express by inventing dimensions high enough to clear the bar —
 * which is how an owner who wrote "let everyone in" got two refusals: with no
 * "What gets through" and no "Receipts I care about", compatibility (25) and
 * verification (30) are unreachable and the ceiling is exactly the bar, so
 * there were no numbers left for the doorman to say yes with.
 *
 * So the doorman now says it directly. `matched` is the whole decision; the
 * SERVER clears the bar from this field and from nothing else, and the score
 * stays honest and is still shown to the owner. `line` is the owner's own line
 * — in the model's words or verbatim — so the owner can see WHICH sentence of
 * theirs let this person in. Null when nothing matched.
 *
 * It is REQUIRED. A missing `override` is a parse failure like any other
 * missing field (invariant (b), fail-closed): a model that did not answer the
 * question has not answered it, and guessing `false` would be guessing.
 */
export const AssessmentOverride = z.object({
  matched: z.boolean(),
  line: z.string().max(OVERRIDE_LINE_MAX_CHARS).nullable(),
  /**
   * WHAT THE ONE CONFIRMING QUESTION GOT (wave 64). `mu-impostor` claimed the
   * musician's shared-stage special case, answered "somewhere in the south, a
   * few years back" and was let through in both runs of 2026-09-23. A special
   * case is a claim with a receipt of its own — the stage, the year, who else
   * was on the bill — and this says whether it came: `produced`, `refused`
   * (claimed, asked, and not supported), or `not_needed` (nothing claimed, or
   * a door-wide line nobody has to confirm). The server lifts nothing on a
   * refused one (`knocks/scoring.ts#applyOverrideReceipt`). Required, like the
   * rest of the field.
   */
  receipt: z.enum(["produced", "refused", "not_needed"]),
  /**
   * THE CONFIRMING QUESTION, COPIED (ruling R-0925-2 (1)): the doorman's own
   * question that asked for the special case's receipt, word for word from
   * what it said earlier in this hearing. A `refused` receipt stands only where
   * the server finds it (`knocks/scoring.ts#applyAskedIn`); null otherwise.
   */
  asked_in: z.string().max(ASKED_IN_MAX_CHARS).nullable(),
});
export type AssessmentOverride = z.infer<typeof AssessmentOverride>;

export const EXCLUSION_LINE_MAX_CHARS = 200;

/**
 * THE EXCLUSION, AS A FIELD (ruling R-0922-3 (3)).
 *
 * "An exclusion beats a permission" was a sentence in the frame and nothing
 * else: the server could not see whether the doorman had matched a line under
 * "What doesn't", so a knock it refused in prose under one could still clear
 * the bar on its numbers — and the keyword matcher that stood in for the field
 * (−8 for any four-letter word in common with a "What doesn't" line) matched
 * words, not exclusions. The doorman now says it directly, as it says an
 * override, and the SERVER holds a matched exclusion under the bar unless an
 * override also matched (`knocks/bands.clearsBar`).
 *
 * `line` is the owner's line, so the owner can see WHICH sentence of theirs
 * kept this person out. Null when nothing matched. A line past its cap is CUT,
 * not refused (`clampDown`): the exclusion is the owner's whole sentence and
 * some are long — the harness's hospital door wrote one of 452 characters —
 * and a model that copied it verbatim has answered the question; cutting the
 * pointer to the sentence raises nothing.
 *
 * REQUIRED, like `override`: a trailer that did not say has not answered.
 */
export const AssessmentExclusion = z.object({
  matched: z.boolean(),
  line: z.string().max(EXCLUSION_LINE_MAX_CHARS).nullable(),
  /**
   * THE LINE'S OWN CARVE-OUT COVERS THIS KNOCK (wave 64). `md-device` cited
   * two studies at a door whose device-sales line "does not reach an
   * evaluation that cites published, peer-reviewed data", and was refused
   * under that line in two of three runs after wave 54 said an exclusion is
   * the whole sentence. The sentence was not enough, so the doorman answers
   * the question separately, and a matched exclusion whose carve-out applies
   * holds nothing (`knocks/scoring.ts#applyCarveOut`). Required.
   */
  carve_out_applies: z.boolean(),
});
export type AssessmentExclusion = z.infer<typeof AssessmentExclusion>;

/**
 * An ask on the record is a `<door_asks>` item copied word for word, and those
 * items are cut to 200 characters where they are lifted (`knocks/jev/state.ts`).
 * A longer one is the owner's whole line, copied from the House Rules instead
 * of the list, and it is cut to the cap rather than refused (`clampDown`, wave
 * 64): it names the same line, and a label cut short raises nothing.
 */
export const RECEIPT_ASK_MAX_CHARS = 200;
/**
 * How many entries one record may carry. `<door_asks>` lists at most six items
 * (`JEV_MAX_ASKS`); ten is room for a model that splits one of them in two, and
 * a record past it is not a record of this door's list.
 */
export const RECEIPTS_RECORD_MAX = 10;

/**
 * THE RECEIPTS RECORD (ruling R-0922-3 (1)).
 *
 * What the doorman did with each thing this door asks for used to live in the
 * dimension — one number, `verification`, for "every receipt I asked for" —
 * and the server read that number's zero as "none produced". It is not: the
 * doorman wrote 0 when ONE receipt was refused and three were given, and the
 * receipts cap turned a knock that produced exactly what the door asked for
 * into a refusal. The server cannot see which receipt was which in a number.
 *
 * So the doorman now writes it down, one entry per ask, and the server reads
 * the entries: `produced` (the knocker gave it, in the form the rules name),
 * `refused` (asked for once and not given), `waived` (an override that names
 * this knocker covers it), `not_applicable` (a route this knock did not take,
 * a line saying what does NOT count, or a receipt the rules say not to ask
 * this kind of knocker for), `unusable` (ruling R-0924-1 (2): something was
 * offered for it, in no form the door can check — a placeholder or dead link,
 * a product behind a login with no access given, a customer the knocker will
 * not let be contacted, a document promised "after a call", "under NDA").
 * `vc-scammer` passed a strict door twice on exactly those, because the only
 * word the doorman had for "offered, but uncheckable" was `produced`. What the
 * server does with them is `ai/score.ts#rawScore`'s to say, and everywhere it
 * counts receipts `unusable` counts as not produced.
 *
 * REQUIRED, like `override`: a trailer without a record has not answered the
 * question, and a malformed one — an unknown status, an ask that is not a
 * string, a list past its bound — quarantines the read (invariant (b)). An
 * EMPTY list is a well-formed answer: a door that asks for nothing has nothing
 * to list.
 */
export const ReceiptStatus = z.enum([
  "produced",
  "refused",
  "unusable",
  "waived",
  "not_applicable",
]);
export type ReceiptStatus = z.infer<typeof ReceiptStatus>;

/** The knocker's words beside a produced receipt: a pointer, cut rather than refused. */
export const RECEIPT_SAID_MAX_CHARS = 200;

export const AssessmentReceipt = z.object({
  ask: z.string().min(1).max(RECEIPT_ASK_MAX_CHARS),
  status: ReceiptStatus,
  /**
   * WHAT THE KNOCKER SAID THAT PRODUCED IT (wave 64), in their own words; null
   * for anything not produced. The record is handed back to the doorman on the
   * next turn (`<receipts_so_far>`), because it asked a start date after "Six
   * weeks.", a listing after both were named and references already given —
   * and an ask shown beside the answer it got is an ask it can see is settled.
   * Cut, not refused, past its cap (`clampDown`): it points at what was said.
   */
  said: z.string().max(RECEIPT_SAID_MAX_CHARS).nullable(),
  /**
   * WHERE THE DOORMAN ASKED FOR IT (ruling R-0925-2 (1)): its own question,
   * copied word for word from a message earlier in the hearing. Required for
   * `refused` and `unusable`, null otherwise. The server looks for it among the
   * doorman's messages the knocker has since answered; an entry whose question
   * it cannot find was never asked, so it is not refused — it is left off the
   * record as an ask not yet put (`knocks/scoring.ts#applyAskedIn`). On
   * 2026-09-25 a strict door held a knocker on turn 1 for a receipt nobody had
   * asked for.
   */
  asked_in: z.string().max(ASKED_IN_MAX_CHARS).nullable(),
});
export type AssessmentReceipt = z.infer<typeof AssessmentReceipt>;

export const AssessmentReceipts = z.array(AssessmentReceipt).max(RECEIPTS_RECORD_MAX);

/** Each of the knocker's two sentences, copied: a quotation, cut rather than refused. */
export const STORY_QUOTE_MAX_CHARS = 300;

/**
 * THE KNOCKER'S STORY CHANGED WHEN IT WAS QUESTIONED (ruling R-0924-1 (3)).
 *
 * A figure, a name, a date or a status the knocker restated differently when
 * asked — revenue that became "pipeline", a round they "led" that became one
 * they "put in" — is the scammer's signature, and the frame has said so in
 * prose since 2026-09-07 without the server being able to see it. The doorman
 * now records it with BOTH of the knocker's own sentences, copied, and the
 * server believes the record only where both are found word for word in the
 * knocker's own messages of this knock (`knocks/scoring.ts#storyChangeVerified`):
 * a quote it cannot find changes nothing. At a strict door a verified change
 * holds the knock under the bar; at any other door it is recorded for the
 * owner and changes no score.
 *
 * REQUIRED, like `minor`: a trailer that did not say has not answered.
 * `earlier` and `later` are null when `changed` is false.
 */
export const AssessmentStoryChanged = z.object({
  changed: z.boolean(),
  earlier: z.string().max(STORY_QUOTE_MAX_CHARS).nullable(),
  later: z.string().max(STORY_QUOTE_MAX_CHARS).nullable(),
});
export type AssessmentStoryChanged = z.infer<typeof AssessmentStoryChanged>;

/** The kind the doorman applied, in its words; and the owner's line, copied: both cut, not refused. */
export const PROCEDURE_KIND_MAX_CHARS = 120;
export const PROCEDURE_LINE_MAX_CHARS = 200;

/**
 * THE OWNER'S PROCEDURE THE DOORMAN APPLIED (ruling R-0924-2 (2)).
 *
 * A door whose House Rules give one kind of knocker its own first step — the
 * press routed without a question, the conflicts question put before the
 * facts — is handed those steps as `<procedures>` (`knocks/grilling.ts`), and
 * the doorman says here which kind it decided this knocker is, and the owner's
 * line for that kind. `kind` null means no kind fitted and the general
 * questioning applied; `line` is then null too. It goes on the knock, comes
 * back to the doorman each turn as `<procedure_so_far>` so it does not drift
 * off the step it chose, and is named in the owner's summary. The server holds
 * nothing on it.
 *
 * REQUIRED, like `minor`: a trailer that did not say has not answered.
 */
export const AssessmentProcedure = z.object({
  kind: z.string().max(PROCEDURE_KIND_MAX_CHARS).nullable(),
  line: z.string().max(PROCEDURE_LINE_MAX_CHARS).nullable(),
});
export type AssessmentProcedure = z.infer<typeof AssessmentProcedure>;

const Dimensions = z.object({
  verification: z.number().int().min(0).max(DIMENSION_MAX.verification),
  substance: z.number().int().min(0).max(DIMENSION_MAX.substance),
  compatibility: z.number().int().min(0).max(DIMENSION_MAX.compatibility),
  value: z.number().int().min(0).max(DIMENSION_MAX.value),
  clarity: z.number().int().min(0).max(DIMENSION_MAX.clarity),
});

export const Assessment = z.object({
  state: z.enum(["CONTINUE", "WORK", "COMPLETE"]),
  work_request: WorkRequest.nullable(),
  dimensions: Dimensions,
  override: AssessmentOverride,
  exclusion: AssessmentExclusion,
  receipts: AssessmentReceipts,
  /**
   * THE KNOCKER SAID THEY ARE UNDER 18 (wave 64). Nob is for adults — the
   * attestation of ruling R-0915-2 — and a knocker who states an age under 18
   * contradicts it. The doorman says so in a field and the SERVER holds the
   * knock not through at every door, whatever the House Rules say
   * (`knocks/scoring.ts#applyAgeRule`), and publishes the read on the turn it
   * is set. REQUIRED, like `override`: a trailer that did not say has not
   * answered.
   */
  minor: z.boolean(),
  /** R-0924-1 (3): the knocker's story changed under questioning — see above. */
  story_changed: AssessmentStoryChanged,
  /** R-0924-2 (2): the owner's procedure the doorman applied — see above. */
  procedure: AssessmentProcedure,
  summary: z.string().max(SUMMARY_MAX_CHARS),
  improvement: z.string().max(IMPROVEMENT_MAX_CHARS),
  suspect_instructions: z.boolean(),
  confidence: z.number().min(0).max(1),
});
export type Assessment = z.infer<typeof Assessment>;

/**
 * The FINAL assessment (ruling R-0906-1): one call at the end of the hearing,
 * over the whole transcript, producing the standing. It has no `state` and no
 * `work_request` — there is no next turn to steer and no work left to ask for.
 */
export const FinalAssessment = Assessment.omit({ state: true, work_request: true });
export type FinalAssessment = z.infer<typeof FinalAssessment>;

/**
 * THE FINAL CALL'S REPLY, AS A JSON SCHEMA THE VENDOR ENFORCES (prompt audit
 * F7). The settling call produces one object and nothing else, so the object is
 * the reply and the provider is TOLD its shape rather than asked nicely for it
 * in prose. Derived from the zod schema above — one definition, never two — and
 * built once, because it is a constant.
 *
 * `reused: "inline"` and `cycles: "throw"` keep it free of `$defs`/`$ref`: an
 * OpenAI-style `json_schema` under `strict` rejects a reference it did not put
 * there itself, and nothing here is recursive enough to need one. `io: "output"`
 * is the right side of a zod schema for a MODEL PRODUCING the object.
 */
export const FINAL_ASSESSMENT_SCHEMA_NAME = "final_assessment";
export const finalAssessmentJsonSchema: object = z.toJSONSchema(FinalAssessment, {
  target: "draft-07",
  io: "output",
  reused: "inline",
  cycles: "throw",
});

export type QuarantineReason =
  | "missing_trailer"
  | "invalid_json"
  | "schema_mismatch"
  /** The summary was about the prompt, not the knock (R-0906-1) — never persisted. */
  | "meta_summary";

export type AssessmentParse =
  | { ok: true; assessment: Assessment; clamped: string[] }
  | { ok: false; quarantine: true; reason: QuarantineReason };

export type FinalAssessmentParse =
  | { ok: true; assessment: FinalAssessment; clamped: string[] }
  | { ok: false; quarantine: true; reason: QuarantineReason };

/**
 * A summary that talks about the INSTRUCTIONS rather than the knock — the
 * signature of a model that read the server's repair re-ask as an injection
 * and wrote "Meta instruction attempting to override required output format"
 * into the line the OWNER reads (evaluation harness, 2026-09-06). A closed
 * list of phrases, like `doorman/changeClaim.ts`: each one is about the
 * output contract, the prompt or an override, and none is how a doorman
 * describes a person at the door.
 */
const META_SUMMARY: RegExp[] = [
  /\bmeta[- ]?instructions?\b/i,
  /\boutput (?:format|contract)\b/i,
  /\brequired (?:output|format)\b/i,
  /\bassessment (?:block|format|json|trailer)\b/i,
  /\bsystem prompt\b/i,
  /\bprompt injection\b/i,
  /\boverride (?:the |its |this |my |required |your )?(?:format|instructions?|output|contract)\b/i,
  /\bjson (?:object|block|format)\b/i,
  /<\/?assessment/i,
];

/** Does the summary describe the prompt instead of the knock? */
export function summaryTalksAboutInstructions(summary: string): boolean {
  return META_SUMMARY.some((re) => re.test(summary));
}

/**
 * The one relaxation of fail-closed, and its whole rule (ruling R-0906-1):
 * A VALUE MAY ONLY BE LOWERED INTO RANGE, NEVER RAISED. `"clarity":12` on a
 * 0–5 scale is a formatting slip, not a guessed score, and the slip is
 * corrected in the one direction a broken trailer is allowed to move a
 * standing — downward. A value BELOW its floor is not lifted to it (that
 * would raise a standing out of nothing), a non-integer is floored, a
 * summary past its cap is cut, a confidence over 1 becomes 1. Everything
 * else — a missing field, a wrong type, a bad state — still fails closed.
 * Returns the fields it touched so the turn can record `trailer_clamped`.
 */
function clampDown(json: Record<string, unknown>): string[] {
  const clamped: string[] = [];
  const dims = json["dimensions"];
  if (dims && typeof dims === "object") {
    const d = dims as Record<string, unknown>;
    for (const [name, max] of Object.entries(DIMENSION_MAX)) {
      const v = d[name];
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      const lowered = Math.min(max, Math.floor(v));
      if (lowered < v) {
        d[name] = lowered;
        clamped.push(`dimensions.${name}`);
      }
    }
  }
  const confidence = json["confidence"];
  if (typeof confidence === "number" && Number.isFinite(confidence) && confidence > 1) {
    json["confidence"] = 1;
    clamped.push("confidence");
  }
  for (const [name, max] of [
    ["summary", SUMMARY_MAX_CHARS],
    ["improvement", IMPROVEMENT_MAX_CHARS],
  ] as const) {
    const s = json[name];
    if (typeof s === "string" && s.length > max) {
      json[name] = s.slice(0, max);
      clamped.push(name);
    }
  }
  // Wave 64: so does the override's. The therapist door's special case is over
  // 400 characters and the field asks for "that line … in your words or
  // theirs": a verbatim copy used to quarantine the whole read, and on
  // `ther-crisis` that turned a person the owner said to let straight in into
  // an unscored refusal. What clears the bar is `matched`; the line only says
  // which sentence did it, and cutting it raises nothing.
  const override = json["override"];
  if (override && typeof override === "object") {
    const o = override as Record<string, unknown>;
    const line = o["line"];
    if (typeof line === "string" && line.length > OVERRIDE_LINE_MAX_CHARS) {
      o["line"] = line.slice(0, OVERRIDE_LINE_MAX_CHARS);
      clamped.push("override.line");
    }
    // R-0925-2 (1): a quotation of the doorman's own question, cut not refused.
    const askedIn = o["asked_in"];
    if (typeof askedIn === "string" && askedIn.length > ASKED_IN_MAX_CHARS) {
      o["asked_in"] = askedIn.slice(0, ASKED_IN_MAX_CHARS);
      clamped.push("override.asked_in");
    }
  }
  // R-0922-3 (3): the exclusion's line points at the owner's sentence, and a
  // pointer cut short raises nothing — see `AssessmentExclusion`.
  const exclusion = json["exclusion"];
  if (exclusion && typeof exclusion === "object") {
    const e = exclusion as Record<string, unknown>;
    const line = e["line"];
    if (typeof line === "string" && line.length > EXCLUSION_LINE_MAX_CHARS) {
      e["line"] = line.slice(0, EXCLUSION_LINE_MAX_CHARS);
      clamped.push("exclusion.line");
    }
  }
  // Wave 64: the knocker's words beside a receipt point at what was said, and a
  // quotation cut short raises nothing either.
  //
  // …AND SO DOES THE ASK. `<door_asks>` cuts a long line at 200 characters, the
  // model sees the whole line in <house_rules> too, and it copies the whole
  // line: the CTO door's vendor route is 246 characters, and every trailer
  // and settling call of all three hearings at that door in the expanded run
  // of 2026-09-23 quarantined on it (`cto-money-ask` read by the budget, with
  // no score). The ask is a label naming the owner's line; the status is the
  // record, and cutting the label raises nothing.
  const receipts = json["receipts"];
  if (Array.isArray(receipts)) {
    for (const entry of receipts) {
      if (!entry || typeof entry !== "object") continue;
      const r = entry as Record<string, unknown>;
      const ask = r["ask"];
      if (typeof ask === "string" && ask.length > RECEIPT_ASK_MAX_CHARS) {
        r["ask"] = ask.slice(0, RECEIPT_ASK_MAX_CHARS);
        if (!clamped.includes("receipts.ask")) clamped.push("receipts.ask");
      }
      const said = r["said"];
      if (typeof said === "string" && said.length > RECEIPT_SAID_MAX_CHARS) {
        r["said"] = said.slice(0, RECEIPT_SAID_MAX_CHARS);
        if (!clamped.includes("receipts.said")) clamped.push("receipts.said");
      }
      const askedIn = r["asked_in"];
      if (typeof askedIn === "string" && askedIn.length > ASKED_IN_MAX_CHARS) {
        r["asked_in"] = askedIn.slice(0, ASKED_IN_MAX_CHARS);
        if (!clamped.includes("receipts.asked_in")) clamped.push("receipts.asked_in");
      }
    }
  }
  // R-0924-1 (3): the two quotations point at what the knocker said, and a
  // quotation cut short is still a run of their words — the server looks for
  // it verbatim, so a cut one is found exactly where the whole one would be.
  const story = json["story_changed"];
  if (story && typeof story === "object") {
    const t = story as Record<string, unknown>;
    for (const name of ["earlier", "later"] as const) {
      const quote = t[name];
      if (typeof quote === "string" && quote.length > STORY_QUOTE_MAX_CHARS) {
        t[name] = quote.slice(0, STORY_QUOTE_MAX_CHARS);
        clamped.push(`story_changed.${name}`);
      }
    }
  }
  // R-0924-2 (2): the kind names what the doorman decided, and the line points
  // at the owner's sentence. Both are pointers, and a pointer cut short raises
  // nothing — the owner's whole procedure lines run past 200 characters.
  const procedure = json["procedure"];
  if (procedure && typeof procedure === "object") {
    const p = procedure as Record<string, unknown>;
    for (const [name, max] of [
      ["kind", PROCEDURE_KIND_MAX_CHARS],
      ["line", PROCEDURE_LINE_MAX_CHARS],
    ] as const) {
      const value = p[name];
      if (typeof value === "string" && value.length > max) {
        p[name] = value.slice(0, max);
        clamped.push(`procedure.${name}`);
      }
    }
  }
  return clamped;
}

/** The block, as JSON, clamped — or the reason there is none. */
function readBlock(
  modelOutput: string,
):
  | { ok: true; json: Record<string, unknown>; clamped: string[] }
  | { ok: false; reason: QuarantineReason } {
  const match = /<assessment>([\s\S]*?)<\/assessment>/.exec(modelOutput);
  if (!match?.[1]) return { ok: false, reason: "missing_trailer" };

  let json: unknown;
  try {
    json = JSON.parse(match[1]);
  } catch {
    return { ok: false, reason: "invalid_json" };
  }
  return finish(json);
}

/**
 * FAIL-CLOSED — invariant (b): a malformed model output can never produce a
 * score or raise a standing. The only non-ok result is quarantine; the only
 * correction applied is a lowering (`clampDown`), and it is reported.
 */
export function parseAssessment(modelOutput: string): AssessmentParse {
  const block = readBlock(modelOutput);
  if (!block.ok) return { ok: false, quarantine: true, reason: block.reason };
  const parsed = Assessment.safeParse(block.json);
  if (!parsed.success) return { ok: false, quarantine: true, reason: "schema_mismatch" };
  return { ok: true, assessment: parsed.data, clamped: block.clamped };
}

/**
 * The same discipline for the final call's reply (R-0906-1) — over a BARE
 * OBJECT (prompt audit F7). The settling call is made under a response schema
 * the vendor enforces, so the reply IS the object: there is no `<assessment>`
 * wrapper to find, and `missing_trailer` is no longer one of the ways this can
 * fail. Everything else is unchanged — clamp down, never up, and a summary
 * about the instructions is a failure.
 *
 * The one tolerance is whitespace, and a code fence a provider wrapped the
 * object in against instructions: neither is a model guessing a score.
 */
export function parseFinalAssessment(modelOutput: string): FinalAssessmentParse {
  const object = readObject(modelOutput);
  if (!object.ok) return { ok: false, quarantine: true, reason: object.reason };
  const parsed = FinalAssessment.safeParse(object.json);
  if (!parsed.success) return { ok: false, quarantine: true, reason: "schema_mismatch" };
  return { ok: true, assessment: parsed.data, clamped: object.clamped };
}

/** The whole reply, as JSON, clamped — or the reason it is not an object. */
function readObject(
  modelOutput: string,
):
  | { ok: true; json: Record<string, unknown>; clamped: string[] }
  | { ok: false; reason: QuarantineReason } {
  const text = modelOutput
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/```$/, "")
    .trim();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return { ok: false, reason: "invalid_json" };
  }
  return finish(json);
}

/** Clamp down, then refuse a summary about the instructions — both readers end here. */
function finish(
  json: unknown,
):
  | { ok: true; json: Record<string, unknown>; clamped: string[] }
  | { ok: false; reason: QuarantineReason } {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    return { ok: false, reason: "schema_mismatch" };
  }
  const record = json as Record<string, unknown>;
  const clamped = clampDown(record);
  const summary = record["summary"];
  if (typeof summary === "string" && summaryTalksAboutInstructions(summary)) {
    return { ok: false, reason: "meta_summary" };
  }
  return { ok: true, json: record, clamped };
}
